import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarType } from 'app/constants/Enums';
import { fileUploadValidator } from 'app/customValidators/fileuploadValidator';
import { formatBytes } from 'app/helpers/filesHelper';
import { SnackBarService } from 'app/services';
import { SupportTicketService } from 'app/services/support-ticket-service/support-ticket.service';
import { environment } from 'environments/environment';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'app-support-ticket-form-dialog',
  standalone: false,
  templateUrl: './support-ticket-form-dialog.component.html',
  styleUrls: ['./support-ticket-form-dialog.component.scss'],
})
export class SupportTicketFormDialogComponent {
  @ViewChild('errorContainer') errorContainer: ElementRef;
  attachments: any[] = [];
  helpDeskConfig = environment.helpDeskConfig;
  allowedExtensions = JSON.parse(this.helpDeskConfig.allowedExtensions);
  maxAttachmentSize= Number.parseInt(this.helpDeskConfig.maxAttachmentSize);
  maxFileCount = Number.parseInt(this.helpDeskConfig.attachmentCount);

  inProgress: boolean = false;
  fileExtensionError: boolean = false;
  showErrorMessage: boolean = false;
  requestFailed:boolean = false;

  supportTicketForm = new FormGroup({
    title: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(Number(this.helpDeskConfig.maxTitleLength)),
    ]),
    description: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(Number(this.helpDeskConfig.maxDescriptionLength)),
    ]),
    files: new FormControl<File[]>(null,[fileUploadValidator(this.maxFileCount,this.maxAttachmentSize)]),
  });

  constructor(
    private translateService: TranslateService,
    private supportTicketService: SupportTicketService,
    private snackbar: SnackBarService,
    private dialogRef: MatDialogRef<SupportTicketFormDialogComponent>,
  ) {}

  addAttachment(event$) {
    this.fileExtensionError = false;
    if (
      event$?.target.files &&
      event$.target.files.length === 1
    ) {
      const files = Array.from((event$.target as HTMLInputElement).files);
      const file = files[0];
      const fileExtension = "." + file.name.split('.').pop()?.toLowerCase();
      if(!this.allowedExtensions.includes(fileExtension)){
        this.fileExtensionError = true;
        return;
      }
      this.removeDuplicates(file);
      this.attachments.push(file);
      this.supportTicketForm.controls['files'].setValue(this.attachments);
    }
    this.getErrorMessage();
  }

  addDragAndDropAttachment(files) {
    this.fileExtensionError = false;
    if (files && files.length === 1 ) {
      const file : File = files[0];
      const fileExtension = "." + file.name.split('.').pop()?.toLowerCase();
      if(!this.allowedExtensions.includes(fileExtension)){
        this.fileExtensionError = true;
        return;
      }
      this.removeDuplicates(file);
      this.attachments.push(file);
      this.supportTicketForm.controls['files'].setValue(this.attachments);
    }
    this.getErrorMessage();
  }

  private removeDuplicates(file: File) {
    if(file){
      let existingFile = this.attachments.find(x=> x.name === file.name);
      if(existingFile){
        this.attachments = this.attachments.filter(x=> x.name !== file.name);
      }
    }
  }

  removeAttachment(attachment: any) {
    this.attachments = this.attachments.filter(
      (file) => file !== attachment,
    );
    this.supportTicketForm.controls['files'].setValue(this.attachments);
    this.getErrorMessage();
  }

  formatFileSize = (bytes) => formatBytes(bytes);

  submit() {
    if (this.supportTicketForm.valid && !this.inProgress) {
      this.inProgress = true;
      this.supportTicketService
        .createSupportTicket(
          this.supportTicketForm.controls.title.value,
          this.supportTicketForm.controls.description.value,
          this.supportTicketForm.controls.files.value,
        )
        .pipe(catchError((error) => this._handleError(error)))
        .subscribe((result) => {
          if (result) {
            let successMsg = '';
            let successTxt = '';

            this.dialogRef.close();

            this.translateService
              .get('SUPPORT.createSuccess')
              .subscribe((value) => (successMsg = value));
            this.translateService
              .get('SNACKBAR.success')
              .subscribe((value) => (successTxt = value));

            this.snackbar.showSnackbar(
              SnackbarType.success,
              successTxt,
              successMsg,
            );
          }
        });
    }
  }

  getErrorMessage(): string {
    let currentShowErrorMessage = this.showErrorMessage;
    this.showErrorMessage = true;

    if (this.supportTicketForm.get('files')?.errors?.maxSize) {
      if(!currentShowErrorMessage){
        this.scrollToElement(this.errorContainer);
      }
      return "SUPPORT.ERROR.file-size";
    }

    if (this.supportTicketForm.get('files')?.errors?.maxFiles) {
      if(!currentShowErrorMessage){
        this.scrollToElement(this.errorContainer);
      }
      return "SUPPORT.ERROR.max-files";
    }

    if(this.requestFailed){
      if(!currentShowErrorMessage){
        this.scrollToElement(this.errorContainer);
      }
      return "SUPPORT.ERROR.generic";
    }

    this.showErrorMessage = false;
    return null;
  }

  private scrollToElement(elementRef : ElementRef){
    elementRef.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
  }

  private _handleError(error: HttpErrorResponse) {
    this.requestFailed = true;
    this.inProgress = false;
    this.getErrorMessage();
    return of();
  }

  preventLeadingWhiteSpace(event$ : KeyboardEvent): void {
    if(event$.target['selectionStart'] === 0){
      event$.preventDefault();
    }
  }

  preventTrailingWhiteSpace(controlName: string): void{
    const control = this.supportTicketForm.get(controlName);
    if(control.value.substring(control.value.length - 2) === "  "){
      control.setValue(control.value.trimEnd()+" ");
    }
  }

  getAllowedExtensions(): string {
    return this.allowedExtensions.join(', ');
  }

  hideErrorElement() : void{
    this.showErrorMessage = false;
    this.requestFailed = false;
  }
}
